import React from 'react';
import { motion } from 'framer-motion';
import { XIcon, MailIcon, BuildingIcon, MapPinIcon, CrownIcon, LogOutIcon, ShieldCheckIcon, CalendarIcon, SparklesIcon, CheckIcon } from 'lucide-react';
import { Button } from './ui/Button';
type ProfilePanelProps = {
  onClose: () => void;
  onLogout: () => void;
};
export function ProfilePanel({
  onClose,
  onLogout
}: ProfilePanelProps) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-50 flex items-start justify-end" onClick={onClose} data-id="element-253">
      <motion.div initial={{
      x: '100%'
    }} animate={{
      x: 0
    }} exit={{
      x: '100%'
    }} transition={{
      type: 'spring',
      damping: 30,
      stiffness: 300
    }} onClick={e => e.stopPropagation()} className="w-full max-w-md h-full bg-slate-900 border-l border-slate-800 overflow-y-auto" data-id="element-254">
        {/* Header */}
        <div className="sticky top-0 bg-slate-900/95 backdrop-blur-sm border-b border-slate-800 p-6 z-10" data-id="element-255">
          <div className="flex items-center justify-between" data-id="element-256">
            <h2 className="text-lg font-semibold text-white" data-id="element-257">Profile</h2>
            <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors" aria-label="Close" data-id="element-258">
              <XIcon className="w-5 h-5" data-id="element-259" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-8" data-id="element-260">
          {/* Avatar & Name */}
          <div className="flex flex-col items-center text-center" data-id="element-261">
            <div className="relative mb-4" data-id="element-262">
              <img src="https://cdn.magicpatterns.com/uploads/8LVD9QTLxrXiDXCudb2s1j/daniel.jpg" alt="Daniel Frerichs" className="w-28 h-28 rounded-full object-cover ring-4 ring-amber-500/20 shadow-[0_0_30px_-5px_rgba(245,158,11,0.2)]" data-id="element-263" />
              <div className="absolute -bottom-1 -right-1 p-1.5 bg-amber-500 rounded-full ring-4 ring-slate-900" data-id="element-264">
                <CrownIcon className="w-3.5 h-3.5 text-slate-900" data-id="element-265" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-white" data-id="element-266">Daniel Frerichs</h3>
            <p className="text-slate-400 mt-1" data-id="element-267">Managing Director</p>
          </div>

          {/* Info Cards */}
          <div className="space-y-3" data-id="element-268">
            <div className="flex items-center gap-3 bg-slate-800/30 border border-slate-700/50 rounded-xl p-4" data-id="element-269">
              <div className="p-2 bg-slate-700/50 rounded-lg" data-id="element-270">
                <MailIcon className="w-4 h-4 text-slate-400" data-id="element-271" />
              </div>
              <div data-id="element-272">
                <p className="text-xs text-slate-500 uppercase tracking-wider" data-id="element-273">
                  Email
                </p>
                <p className="text-sm text-white" data-id="element-274">
                  daniel.frerichs@asia-pilots.com
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 bg-slate-800/30 border border-slate-700/50 rounded-xl p-4" data-id="element-275">
              <div className="p-2 bg-slate-700/50 rounded-lg" data-id="element-276">
                <BuildingIcon className="w-4 h-4 text-slate-400" data-id="element-277" />
              </div>
              <div data-id="element-278">
                <p className="text-xs text-slate-500 uppercase tracking-wider" data-id="element-279">
                  Company
                </p>
                <p className="text-sm text-white" data-id="element-280">Asiapilots Ltd.</p>
              </div>
            </div>

            <div className="flex items-center gap-3 bg-slate-800/30 border border-slate-700/50 rounded-xl p-4" data-id="element-281">
              <div className="p-2 bg-slate-700/50 rounded-lg" data-id="element-282">
                <MapPinIcon className="w-4 h-4 text-slate-400" data-id="element-283" />
              </div>
              <div data-id="element-284">
                <p className="text-xs text-slate-500 uppercase tracking-wider" data-id="element-285">
                  Location
                </p>
                <p className="text-sm text-white" data-id="element-286">Hamburg, Germany</p>
              </div>
            </div>
          </div>

          {/* Subscription Card */}
          <div className="relative overflow-hidden rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-slate-900 to-slate-900" data-id="element-287">
            <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/10 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2" data-id="element-288" />
            <div className="relative p-6" data-id="element-289">
              <div className="flex items-center gap-3 mb-4" data-id="element-290">
                <div className="p-2 bg-amber-500/20 rounded-lg" data-id="element-291">
                  <SparklesIcon className="w-5 h-5 text-amber-500" data-id="element-292" />
                </div>
                <div data-id="element-293">
                  <div className="flex items-center gap-2" data-id="element-294">
                    <h4 className="text-lg font-bold text-white" data-id="element-295">
                      Premium Plus
                    </h4>
                    <span className="px-2 py-0.5 bg-amber-500 text-slate-900 text-xs font-bold rounded-full uppercase" data-id="element-296">
                      Active
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5" data-id="element-297">
                    Enterprise subscription
                  </p>
                </div>
              </div>

              <div className="space-y-2.5 mb-5" data-id="element-298">
                {['Unlimited company searches', 'Full innovation database access', 'Advanced analytics & reports', 'API access & integrations', 'Priority support'].map(feature => <div key={feature} className="flex items-center gap-2.5" data-id="element-299">
                    <div className="p-0.5 bg-amber-500/20 rounded-full" data-id="element-300">
                      <CheckIcon className="w-3 h-3 text-amber-500" data-id="element-301" />
                    </div>
                    <span className="text-sm text-slate-300" data-id="element-302">{feature}</span>
                  </div>)}
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-slate-700/50" data-id="element-303">
                <div className="flex items-center gap-2 text-slate-400" data-id="element-304">
                  <CalendarIcon className="w-4 h-4" data-id="element-305" />
                  <span className="text-xs" data-id="element-306">Renews Jan 15, 2027</span>
                </div>
                <div className="flex items-center gap-1.5" data-id="element-307">
                  <ShieldCheckIcon className="w-4 h-4 text-emerald-500" data-id="element-308" />
                  <span className="text-xs text-emerald-400 font-medium" data-id="element-309">
                    Verified
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Logout */}
          <div className="pt-4 border-t border-slate-800" data-id="element-310">
            <Button onClick={onLogout} variant="outline" className="w-full h-11 gap-2 border-red-900/50 text-red-400 hover:bg-red-900/20 hover:text-red-300 hover:border-red-800" data-id="element-311">
              <LogOutIcon className="w-4 h-4" data-id="element-312" />
              Sign Out
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>;
}